import { getField, updateField } from 'vuex-map-fields';
import { statistics, users } from '~api';
import dayjs from 'dayjs';
import i18n from '../i18n';

const SET_LABELS = 'SET_LABELS';
const SET_RANGE = 'SET_RANGE';
const SET_MINUTES_MEDITATED = 'SET_MINUTES_MEDITATED';
const SET_MEDITATIONS_MEDITATED = 'SET_MEDITATIONS_MEDITATED';
const SET_USER = 'SET_USER';
const SET_PERIOD = 'SET_PERIOD';
const SET_DIFF = 'SET_DIFF';
const SET_DATA_COLLECTION = 'SET_DATA_COLLECTION';
const SET_FILTERS = 'SET_FILTERS';
const SET_USER_COUNT = 'SET_USER_COUNT';
const SET_VIEWS = 'SET_VIEWS';
const SET_DAILY_VIEWS = 'SET_DAILY_VIEWS';
const RESET_PERIOD = 'RESET_PERIOD';
const RESET_FILTERS = 'RESET_FILTERS';
const SET_LONGEST_PRACTICE = 'SET_LONGEST_PRACTICE';
const SET_AMPLITUDE_GRAPH = 'SET_AMPLITUDE_GRAPH';

const defaultDataCollection = {
    labels: i18n.t('statistics.weekDays'),
    datasets: [
        {
            label: 'Before Session',
            data: [],
            borderColor: '#E5BFB6',
            pointHitRadius: 20,
            pointBackgroundColor: '#E5BFB6',
            borderWidth: 3,
            backgroundColor: 'rgba(0, 0, 0, 0)'
        },
        {
            label: 'After Session',
            data: [],
            borderColor: '#7096D2',
            pointBackgroundColor: '#7096D2',
            pointHitRadius: 20,
            borderWidth: 3,
            backgroundColor: 'rgba(0, 0, 0, 0)'
        }
    ]
};

const state = {
    amplitudeGraph: null,
    uniqueUsers: null,
    period: 'lifetime',
    userCount: 0,
    user: null,
    diff: null,
    minutesMeditated: 0,
    meditationsMeditated: 0,
    range: {
        from: dayjs().startOf('week'),
        to: dayjs().endOf('week')
    },
    filter: {
        updated: null,
        groupBy: 'daily',
        businessId: null,
        userId: null,
        voteDateFrom: dayjs()
            .startOf('week')
            .format('YYYY-MM-DDT00:00:00[Z]'),
        voteDateTo: dayjs()
            .endOf('week')
            .format('YYYY-MM-DDT23:59:59[Z]')
    },
    dataCollection: { ...defaultDataCollection },
    views: {
        total_sessions: 0,
        total_sessions_live: 0,
        total_sessions_recorded: 0,
        total_time: 0,
        total_time_live: 0,
        total_time_recorded: 0,
        total_views: 0,
        total_views_live: 0,
        total_views_recorded: 0,
        total_complete_views: 0,
        total_complete_views_live: 0,
        total_complete_views_recorded: 0,
        total_complete_meditations: 0,
        longest_practice: 0
    },
    totalDailyViews: 0,
    votes: []
};

const actions = {
    /**
     * Update date range
     * @param {function} commit
     * @param {object} state
     * @param {function} dispatch
     * @param {string} id
     * @returns {Promise<void>}
     */
    async updateRange({ commit, state, dispatch, rootGetters }, id) {
        /**
         * There are 4 different periods:
         * - week
         * - month
         * - year
         * - range
         * First 3 periods we can receive when `Actor` click on one of radio buttons on statistics page.
         * Period `range` we receive when `Actor` choose date from and date to.
         */

        const localUser = rootGetters['auth/user'];

        let period = state.period,
            labels = [],
            labelsDates = [],
            range,
            diff,
            diffParam,
            groupBy,
            start,
            end;

        if (period !== 'range') {
            range = {
                from: dayjs().startOf(period),
                to: dayjs().endOf(period)
            };

            await commit(SET_RANGE, range);
        }

        // Determine start and end of range
        switch (period) {
            case 'lifetime': {
                range = {
                    from: dayjs(localUser?.createdAt)
                        .startOf('month')
                        .format('MM/DD/YYYY'),
                    to: dayjs()
                        .endOf('month')
                        .format('MM/DD/YYYY')
                };
                await commit(SET_RANGE, range);
                break;
            }
            case 'year': {
                range = {
                    from: dayjs()
                        .subtract(1, 'year')
                        .format('MM/DD/YYYY'),
                    to: dayjs().format('MM/DD/YYYY')
                };
                await commit(SET_RANGE, range);
                break;
            }
            case 'month': {
                range = {
                    from: dayjs()
                        .subtract(1, 'month')
                        .format('MM/DD/YYYY'),
                    to: dayjs().format('MM/DD/YYYY')
                };
                await commit(SET_RANGE, range);
                break;
            }
            default: {
                break;
            }
        }

        const monthFrom = dayjs(state.range.from).month() + 1;
        const monthTo = dayjs(state.range.to).month() + 1;
        const yearFrom = dayjs(state.range.from).year();
        const yearTo = dayjs(state.range.to).year();

        // By checking period we set labels for chart, `groupBy` and `diffParam` parameters
        switch (state.period) {
            case 'week': {
                labels = i18n.t('statistics.weekDays');

                start = state.range.from;
                end = state.range.to;
                while (dayjs(start).isBefore(dayjs(end))) {
                    labelsDates.push(dayjs(start).format('YYYY-MM-DD'));
                    start = dayjs(start).add(1, 'day');
                }
                groupBy = 'daily';
                break;
            }

            case 'month': {
                let runningDate = dayjs(state.range.from);
                // Add an hour to include the current day in the loop below
                const targetDate = dayjs(state.range.to).add(1, 'hour');

                // Prevent infinite loops
                let loopCounter = 0;

                while (runningDate.isBefore(targetDate) && loopCounter++ < 50) {
                    labelsDates.push(runningDate.format('YYYY-MM-DD'));
                    labels.push(runningDate.format('DD'));
                    runningDate = runningDate.add(1, 'day');
                }

                groupBy = 'daily';
                break;
            }

            case 'year': {
                let runningDate = dayjs(state.range.from);
                // Add a day to include the current month in the following loop
                const targetDate = dayjs(state.range.to).add(1, 'day');

                // Prevent infinite loops
                let loopCounter = 0;

                while (runningDate.isBefore(targetDate) && loopCounter++ < 50) {
                    labelsDates.push(runningDate.format('YYYY-MM'));
                    labels.push(runningDate.format('MMM'));
                    runningDate = runningDate.add(1, 'month');
                }

                groupBy = 'monthly';
                break;
            }

            default:
                if (monthFrom === monthTo && yearFrom === yearTo) {
                    for (
                        let i = dayjs(state.range.from).date();
                        i <= dayjs(state.range.to).date();
                        i++
                    ) {
                        labels.push(`${monthFrom}/${i}`);
                        let day = i < 10 ? `0${i}` : i;
                        if (monthFrom < 10) {
                            labelsDates.push(
                                `${yearFrom}-0${monthFrom}-${day}`
                            );
                        } else {
                            labelsDates.push(`${yearFrom}-${monthFrom}-${day}`);
                        }
                    }

                    diffParam = 'day';
                    groupBy = 'daily';
                } else if (yearFrom === yearTo) {
                    for (let i = monthFrom; i <= monthTo; i++) {
                        labels.push(`${i}/${yearFrom}`);
                        if (i < 10) {
                            labelsDates.push(`${yearFrom}-0${i}`);
                        } else {
                            labelsDates.push(`${yearFrom}-${i}`);
                        }
                    }
                    diffParam = 'month';
                    groupBy = 'monthly';
                } else if (
                    yearFrom !== yearTo &&
                    12 - monthFrom + monthTo <= 12 &&
                    yearTo - yearFrom === 1
                ) {
                    for (let i = monthFrom; i <= 12; i++) {
                        labels.push(`${i}/${yearFrom}`);
                        if (i < 10) {
                            labelsDates.push(`${yearFrom}-0${i}`);
                        } else {
                            labelsDates.push(`${yearFrom}-${i}`);
                        }
                    }

                    for (let i = 1; i <= monthTo; i++) {
                        labels.push(`${i}/${yearTo}`);
                        if (i < 10) {
                            labelsDates.push(`${yearTo}-0${i}`);
                        } else {
                            labelsDates.push(`${yearTo}-${i}`);
                        }
                    }
                    diffParam = 'month';
                    groupBy = 'monthly';
                } else {
                    for (let i = yearFrom; i <= yearTo; i++) {
                        labels.push(i);
                        labelsDates.push(i);
                    }
                    diffParam = 'year';
                    groupBy = 'yearly';
                }

                diff = dayjs(state.range.to).diff(
                    dayjs(state.range.from),
                    diffParam
                );

                diff = diff + 1;

                commit(SET_DIFF, diff);
                break;
        }

        const filterParams = {
            groupBy,
            voteDateFrom: dayjs(state.range.from).format(
                'YYYY-MM-DDT00:00:00[Z]'
            ),
            voteDateTo: dayjs(state.range.to).format('YYYY-MM-DDT23:59:59[Z]'),
            interval: state.period
        };

        // If exist userId than get statistics by user
        if (id || state.filter.userId) {
            filterParams.userId = id;
        }

        // If exist businessId than get statistics by Business
        if (state.filter.businessId) {
            filterParams.businessId = state.filter.businessId;
        }

        // If exist department than get statistics by Business and Department
        if (state.filter.department) {
            filterParams.department = state.filter.department;
        }

        // If exist custom field than get statistics by Custom field
        if (state.filter.customFields) {
            filterParams.customFields = state.filter.customFields;
        }

        await commit(SET_FILTERS, filterParams);

        dispatch(
            'statistics/getData',
            { labels, labelsDates, id },
            { root: true }
        );

        dispatch('statistics/getMinutesMeditated', null, { root: true });
        dispatch('statistics/getMeditationsMeditated', null, { root: true });
    },
    async getMinutesMeditated({ commit, state }) {
        const params = {};

        params.dateRangeType = 'lifetime';
        params.dateFrom = '2017-10-10';
        params.dateTo = dayjs(state.range.to).format('YYYY-MM-DD');

        if (state.period !== 'lifetime') {
            params.dateRangeType = state.period;
            params.dateFrom = dayjs(state.range.from).format('YYYY-MM-DD');
            params.dateTo = dayjs(state.range.to).format('YYYY-MM-DD');
        }

        const resp = await statistics.totalMinutes(params);

        if (resp.ok) {
            const total = Math.round(resp.data.total / 60);
            commit(SET_MINUTES_MEDITATED, total);
        }
    },
    async getMeditationsMeditated({ commit, state }) {
        const params = {};

        params.dateRangeType = 'lifetime';
        params.dateFrom = '2017-10-10';
        params.dateTo = dayjs(state.range.to).format('YYYY-MM-DD');

        if (state.period !== 'lifetime') {
            params.dateRangeType = state.period;
            params.dateFrom = dayjs(state.range.from).format('YYYY-MM-DD');
            params.dateTo = dayjs(state.range.to).format('YYYY-MM-DD');
        }

        const resp = await statistics.totalWatches(params);

        if (resp.ok) {
            commit(SET_MEDITATIONS_MEDITATED, resp.data.total);
        }
    },
    setRange({ commit, dispatch }, data) {
        const { from, to } = data;

        commit(SET_RANGE, { from, to });
        commit(SET_PERIOD, 'range');
        dispatch('updateRange', data.id);
    },
    async getData({ commit, state }, data) {
        let votesData, dailyViewsData, viewsData;

        if (data.id) {
            votesData = await statistics.moodData(state.filter, data.id);
            dailyViewsData = await statistics.dailyViewsData(
                state.filter,
                data.id
            );
            viewsData = await statistics.viewsData(state.filter, data.id);

            const user = await users.read(data.id);

            if (user && user.data) {
                commit(SET_USER, user.data);
            } else {
                commit(SET_USER, {});
            }
        } else {
            votesData = await statistics.moodData(state.filter);
            dailyViewsData = await statistics.dailyViewsData(state.filter);
            viewsData = await statistics.viewsData(state.filter);

            commit(SET_USER, {});
        }
        const longestPractice = await statistics.longestPractice();
        const { labels, labelsDates } = data;

        if (votesData.ok) {
            const { votes, userCount, amplitudeGraph } = votesData.data;
            commit(SET_AMPLITUDE_GRAPH, amplitudeGraph);
            const views = viewsData?.data;

            // Convert all properties to integer
            for (const prop in views) {
                views[prop] = +views[prop];
            }

            const dailyViews = dailyViewsData.data;

            let beforeData = labelsDates.reduce(
                (o, key) => Object.assign(o, { [key]: null }),
                {}
            );
            let afterData = labelsDates.reduce(
                (o, key) => Object.assign(o, { [key]: null }),
                {}
            );

            if (votes && votes.length) {
                votes.map(vote => {
                    const date = vote.date;

                    beforeData[date] = vote.before;
                    afterData[date] = vote.after;
                });
            }

            beforeData = Object.values(beforeData);
            afterData = Object.values(afterData);

            commit(SET_DATA_COLLECTION, { beforeData, afterData, labels });
            commit(SET_USER_COUNT, userCount);
            commit(SET_VIEWS, { views, votes });
            commit(SET_DAILY_VIEWS, dailyViews);
            commit(SET_LONGEST_PRACTICE, longestPractice.data);
        }
    },
    resetPeriod({ commit }) {
        commit(RESET_PERIOD);
    },
    resetFilters({ commit }) {
        commit(RESET_FILTERS);
    }
};

const mutations = {
    // Add the `updateField` mutation to the
    // `mutations` of your Vuex store instance.
    updateField,
    [SET_LABELS](state, data) {
        state.xAxisLabels = data;
        return true;
    },
    [SET_RANGE](state, data) {
        state.range = data;
    },
    [SET_PERIOD](state, data) {
        state.period = data;
    },
    [SET_DIFF](state, data) {
        state.diff = data;
    },
    [SET_DATA_COLLECTION](state, data) {
        const { beforeData, afterData, labels } = data;

        state.dataCollection.labels = labels;
        state.dataCollection.updated = dayjs().valueOf();
        state.dataCollection.datasets[0].data = beforeData;
        state.dataCollection.datasets[1].data = afterData;
    },
    [SET_FILTERS](state, data) {
        state.filter = data;
        return true;
    },
    [SET_USER_COUNT](state, data) {
        state.userCount = data;
    },
    [SET_MINUTES_MEDITATED](state, data) {
        state.minutesMeditated = data;
    },
    [SET_MEDITATIONS_MEDITATED](state, data) {
        state.meditationsMeditated = data;
    },
    [SET_USER](state, data) {
        state.user = data;
    },
    [SET_DAILY_VIEWS](state, data) {
        state.totalDailyViews = data;
    },
    [SET_LONGEST_PRACTICE](state, data) {
        state.views.longest_practice = data;
    },
    [SET_VIEWS](state, data) {
        state.views = data.views;
    },
    [SET_AMPLITUDE_GRAPH](state, data) {
        if (!data || !data.data?.series[0]) return;
        const series = data.data?.series[0];
        const checkZeros = () => {
            if (series[series.length - 1] === 0) {
                series.pop();
                checkZeros();
            }
        };
        checkZeros();
        state.amplitudeGraph = {
            labels: data.data?.xValues.map(date =>
                dayjs(date).format('MM/DD/YY')
            ),
            datasets: [
                {
                    label: 'Users',
                    data: series,
                    borderColor: '#A5BEA4',
                    pointBackgroundColor: '#B1BEA9',
                    pointHitRadius: 20,
                    borderWidth: 3,
                    backgroundColor: 'rgba(0, 0, 0, 0)'
                }
            ]
        };

        state.uniqueUsers = data.data?.seriesCollapsed[0][0].value;
    },
    [RESET_PERIOD](state) {
        state.period = 'lifetime';
        state.range = {
            from: dayjs()
                .startOf('week')
                .format('YYYY-MM-DDT00:00:00[Z]'),
            to: dayjs()
                .endOf('week')
                .format('YYYY-MM-DDT23:59:59[Z]')
        };
    },
    [RESET_FILTERS](state) {
        state.filter = {
            updated: null,
            groupBy: 'daily',
            businessId: null,
            userId: null,
            voteDateFrom: dayjs()
                .startOf('week')
                .format('YYYY-MM-DDT00:00:00[Z]'),
            voteDateTo: dayjs()
                .endOf('week')
                .format('YYYY-MM-DDT23:59:59[Z]')
        };
    }
};

const getters = {
    // Add the `getField` getter to the
    // `getters` of your Vuex store instance.
    getField,
    period: state => state.period,
    range: state => state.range,
    minutesMeditated: state => state.minutesMeditated,
    meditationsMeditated: state => state.meditationsMeditated,
    dataCollection: state => state.dataCollection,
    diff: state => state.diff,
    counter: state => state.userCount,
    userName: state =>
        state.user && state.user.fullName ? state.user.fullName : null,
    views: state => state.views,
    amplitudeGraph: state => state.amplitudeGraph,
    uniqueUsers: state => state.uniqueUsers,
    totalDailyViews: state =>
        state.totalDailyViews ? state.totalDailyViews : 0
};

export default {
    namespaced: true,
    state,
    actions,
    mutations,
    getters
};
